import lod_ from "lodash";
import {
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Icon,
	List,
	ListItem
} from "@mui/material";
import MDBox from "components/Basics/MDBox";
import { t } from "i18next";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import FormActions from "redux-react/actions/formAction";
import SidenavContainer from "../Components/SidenavContainer";
import MDInput from "components/Basics/MDInput";
import MDTypography from "components/Basics/MDTypography";
import colors from "assets/theme/base/colors";
import MDButton from "components/Basics/MDButton";
import { display } from "redux-react/reducers/snackBarReducer";

/**
 * Import charts from catalog dialog
 * @param {boolean} open - Open state
 * @param {function} onClose - Close function
 * @param {string} currentPage - Current page to import charts
 * @param {function} reloadPage - Function to reload the page after import
 * @returns {JSX.Element} - The ImportChartsDialog component
 */
const ImportChartsDialog = ({ open, onClose, currentPage, reloadPage }) => {
	const dispatch = useDispatch();

	const [selectedRows, setSelectedRows] = useState([]);
	const [selectedTags, setSelectedTags] = useState([]);
	const [searchTextTags, setSearchTextTags] = useState("");
	const [searchText, setSearchText] = useState("");

	const [charts, setCharts] = useState([]);
	const [tags, setTags] = useState([]);

	/**
	 * Get the count of charts for a tag
	 */
	const getTagsCount = tag => {
		const chartTags = charts.display?.tags || [];
		const count = charts.filter(chart => chartTags.includes(tag)).length;
		return count;
	};

	const loadCharts = () => {
		dispatch(
			FormActions.getItemsFromCollection(
				"analytics",
				{
					account: "catalog",
					query: {
						documentType: "chart",
						active: { $ne: false }
					},
					projection: {}
				},
				({ items: catalogCharts }) => {
					setCharts(catalogCharts);
					// Set of all tags
					const possibleTags = [
						...new Set(catalogCharts.flatMap(chart => chart.display.tags || []))
					];
					possibleTags.sort();
					setTags(possibleTags);
				}
			)
		);
	};

	/**
	 * Import charts in the current page
	 */
	const importCharts = () => {
		dispatch(
			FormActions.importFromCatalog(
				{
					collection: "analytics",
					items: selectedRows,
					update: {
						active: true,
						"display.pages": [currentPage]
					},
					actions: [],
					deleteQuery: {
						documentType: "chart",
						"display.pages": { $in: [currentPage] }
					}
				},
				() => {
					reloadPage();
				},
				() => {
					dispatch(
						display({
							message: t("FORMS.importError"),
							type: "error"
						})
					);
				}
			)
		);
	};

	// Filters with tags and search text
	const filteredCharts = useMemo(() => {
		return charts.filter(chart => {
			const chartTags = chart.display.tags || [];
			// Filter by tags
			if (
				selectedTags.length > 0 &&
				(lod_.isNil(chartTags) || !chartTags.some(tag => selectedTags.includes(tag)))
			) {
				return false;
			}

			const title = chart.display.title || "";
			const description = chart.display.description || "";
			const code = chart.code || "";
			// Filter by search text
			if (
				searchText &&
				!title.toLowerCase().includes(searchText.toLowerCase()) &&
				!description.toLowerCase().includes(searchText.toLowerCase()) &&
				!code.toLowerCase().includes(searchText.toLowerCase())
			) {
				return false;
			}

			return true;
		});
	}, [charts, selectedTags, searchText]);

	useEffect(() => {
		// Reset variables
		setSelectedRows([]);
		setSelectedTags([]);
		setSearchTextTags("");
		setSearchText("");
		setCharts([]);
		setTags([]);
		// Load charts
		if (open) {
			loadCharts();
		}
	}, [open]);

	return (
		<Dialog
			fullWidth
			maxWidth="lg"
			open={open}
			PaperProps={{
				style: {
					height: "100%",
					width: "100%",
					maxWidth: "90%"
				}
			}}
		>
			<DialogTitle>{t("SETTINGS.CHARTS.importChartsFromCatalog")}</DialogTitle>
			<DialogContent
				style={{
					display: "flex",
					overflowY: "auto"
				}}
			>
				<MDBox mt={1} display="flex" flex="1">
					<SidenavContainer>
						<List sx={{ pr: 1 }}>
							<ListItem sx={{ mb: 1 }}>
								<MDInput
									fullWidth
									placeholder={t("SETTINGS.PAGES.searchTags")}
									value={searchTextTags}
									onChange={e => setSearchTextTags(e.target.value)}
								/>
							</ListItem>
							{tags
								.filter(tag => tag.toLowerCase().includes(searchTextTags.toLowerCase()))
								.map(tag => (
									<ListItem
										key={tag}
										onClick={() => {
											if (selectedTags.includes(tag)) {
												setSelectedTags(prev => prev.filter(t => t !== tag));
											} else {
												setSelectedTags(prev => [...prev, tag]);
											}
										}}
										sx={{
											userSelect: "none",
											cursor: "pointer",
											transition: "background-color 0.1s",
											backgroundColor: selectedTags.includes(tag)
												? "rgba(0, 0, 0, 0.06)"
												: "inherit",
											"&:hover": {
												backgroundColor: "rgba(0, 0, 0, 0.06)"
											},
											borderRadius: "8px"
										}}
									>
										<Checkbox checked={selectedTags.includes(tag)} />
										<MDTypography variant="body2" fontWeight="regular" color="text">
											{tag} ({getTagsCount(tag)})
										</MDTypography>
									</ListItem>
								))}
						</List>
					</SidenavContainer>
					{/* Content */}
					<MDBox flex="1" display="flex" flexDirection="column">
						<MDBox>
							<MDInput
								fullWidth
								placeholder={t("SETTINGS.CHARTS.searchGraph")}
								value={searchText}
								onChange={e => setSearchText(e.target.value)}
							/>
						</MDBox>
						{/* Empty display */}
						{lod_.isEmpty(filteredCharts) && (
							<MDBox
								flex="1"
								display="flex"
								flexDirection="column"
								alignItems="center"
								justifyContent="center"
							>
								<MDTypography variant="body2" color="textSecondary">
									{t("SETTINGS.CHARTS.noChartsAvailable")}
								</MDTypography>
							</MDBox>
						)}
						<MDBox
							mt={2}
							display="grid"
							gridTemplateColumns="repeat(3, 1fr)"
							gap={2}
							sx={{
								overflowY: "auto"
							}}
						>
							{/* Real charts */}
							{filteredCharts.map(chart => {
								const isSelected = selectedRows.includes(chart.code);

								return (
									<MDBox
										key={chart.code}
										border="1px solid #ddd"
										borderRadius="8px"
										display="flex"
										flexDirection="column"
										alignItems="center"
										sx={{
											backgroundColor: isSelected ? colors.info.main : "white",
											transition: "all 0.2s",
											boxSizing: "border-box",
											"&:hover": {
												cursor: "pointer",
												border: "1px solid #aaa",
												boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)"
											}
										}}
										onClick={() => {
											if (isSelected) {
												setSelectedRows(prev => prev.filter(code => code !== chart.code));
											} else {
												setSelectedRows(prev => [...prev, chart.code]);
											}
										}}
									>
										{!lod_.isNil(chart.display.image) && !lod_.isEmpty(chart.display.image) ? (
											<MDBox
												sx={{
													height: "10rem",
													overflow: "hidden",
													borderRadius: "8px 8px 0 0"
												}}
											>
												<img
													src={chart.display.image}
													alt={chart.display.title}
													style={{ width: "100%", height: "100%", objectFit: "cover" }}
												/>
											</MDBox>
										) : (
											<MDBox
												sx={{
													width: "100%",
													height: "10rem",
													display: "flex",
													justifyContent: "center",
													alignItems: "center"
												}}
											>
												<MDBox
													bgColor={chart?.display?.icon?.color || "info"}
													variant="gradient"
													coloredShadow={chart?.display?.icon?.color || "info"}
													borderRadius="xl"
													display="flex"
													justifyContent="center"
													alignItems="center"
													width="3rem"
													height="3rem"
													color="white"
												>
													<Icon fontSize="small" color="inherit">
														{chart?.display?.icon?.component || "show_chart"}
													</Icon>
												</MDBox>
											</MDBox>
										)}
										<MDBox
											sx={{
												boxShadow: "inset 0px 5px 5px -5px rgba(0,0,0,0.2)",
												flex: 1,
												width: "100%",
												p: 1
											}}
										>
											<MDTypography variant="h6" color={isSelected ? "white" : "dark"}>
												{chart.display.title}
											</MDTypography>
											<MDTypography variant="body2" color={isSelected ? "white" : "text"}>
												{chart.display.description}
											</MDTypography>
										</MDBox>
									</MDBox>
								);
							})}
						</MDBox>
					</MDBox>
				</MDBox>
			</DialogContent>
			<DialogActions>
				<MDButton variant="outlined" color="info" onClick={onClose}>
					{t("SETTINGS.close")}
				</MDButton>
				<MDButton
					disabled={selectedRows.length === 0}
					variant="contained"
					color="info"
					onClick={importCharts}
				>
					{`${t("SETTINGS.import")} (${selectedRows.length})`}
				</MDButton>
			</DialogActions>
		</Dialog>
	);
};

export default ImportChartsDialog;
